import { useEffect, useRef } from 'react';

/**
 * Custom event name dispatched by SocketContext when a chat message arrives
 * while the user is NOT focused on the chat input.
 */
export const DUCKING_EVENT = 'bandmate:chat-ducking';

/** Duck to this fraction of the current volume (0.3 = 30%). */
const DUCK_RATIO = 0.3;
/** How long to hold the ducked volume before restoring (ms). */
const DUCK_HOLD_MS = 2000;
/** Duration of the fade-back-up transition (ms). */
const RESTORE_FADE_MS = 500;
/** Minimum interval between ducks to prevent rapid re-triggering (ms). */
const DUCK_COOLDOWN_MS = 1500;

/** Quadratic ease-out: fast start, smooth deceleration. */
const easeOutQuad = (t: number) => 1 - (1 - t) * (1 - t);

export interface VolumeControl {
  /** Read the current intended volume (ignoring mute). */
  getVolume: () => number;
  /** Apply a volume value to the underlying audio output. */
  setVolume: (v: number) => void;
  /** Whether audio is currently muted (ducking is skipped when muted). */
  isMuted?: () => boolean;
}

/**
 * Subscribes to the global `bandmate:chat-ducking` event and temporarily
 * lowers the audio volume when a chat message is received.
 *
 * Accepts a generic `VolumeControl` so it works for both HTML `<audio>`
 * elements (AudioPlayer) and WaveSurfer instances (Waveform).
 *
 * @param control - Volume getter/setter for the active audio source.
 * @param enabled - Whether ducking is currently active (e.g. only when playing).
 */
export function useAudioDucking(control: VolumeControl, enabled: boolean): void {
  const restoreTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafId = useRef<number | null>(null);
  const isDucked = useRef(false);
  const lastDuckTime = useRef(0);
  const preduckedVolume = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled) return;

    const handleDuck = () => {
      // Skip ducking if the audio is muted
      if (control.isMuted?.()) return;

      const now = Date.now();
      if (now - lastDuckTime.current < DUCK_COOLDOWN_MS) return;
      lastDuckTime.current = now;

      // Cancel any in-flight restore so we don't fight ourselves
      if (restoreTimer.current) clearTimeout(restoreTimer.current);
      if (rafId.current) cancelAnimationFrame(rafId.current);

      const originalVolume = isDucked.current && preduckedVolume.current !== null
        ? preduckedVolume.current
        : control.getVolume();

      preduckedVolume.current = originalVolume;
      isDucked.current = true;

      // Immediately drop to ducked level
      const duckedVolume = originalVolume * DUCK_RATIO;
      control.setVolume(duckedVolume);

      // After the hold period, smoothly restore
      restoreTimer.current = setTimeout(() => {
        const startVolume = duckedVolume;
        const startTime = performance.now();

        const tick = (now: number) => {
          const elapsed = now - startTime;
          const progress = Math.min(elapsed / RESTORE_FADE_MS, 1);
          // Ease-out quadratic for a natural fade-up
          const eased = easeOutQuad(progress);
          const currentVolume = startVolume + (originalVolume - startVolume) * eased;

          control.setVolume(currentVolume);

          if (progress < 1) {
            rafId.current = requestAnimationFrame(tick);
          } else {
            isDucked.current = false;
            preduckedVolume.current = null;
          }
        };

        rafId.current = requestAnimationFrame(tick);
      }, DUCK_HOLD_MS);
    };

    window.addEventListener(DUCKING_EVENT, handleDuck);

    return () => {
      window.removeEventListener(DUCKING_EVENT, handleDuck);
      if (restoreTimer.current) clearTimeout(restoreTimer.current);
      if (rafId.current) cancelAnimationFrame(rafId.current);

      // Restore volume if we're mid-duck when the component unmounts
      if (isDucked.current && preduckedVolume.current !== null) {
        control.setVolume(preduckedVolume.current);
        isDucked.current = false;
        preduckedVolume.current = null;
      }
    };
  }, [enabled, control]);
}
